import React from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MessageCircle, MailWarning, Snowflake, type LucideIcon } from "lucide-react";
import {
  buildExpiredMessage,
  buildUnconfirmedMessage,
  buildColdLeadMessage,
  buildWhatsAppUrl,
  normalizeWhatsAppNumber,
} from "@/utils/whatsappTemplates";
import { recordAdminContact } from "@/utils/adminContacts";

type ContactType = "expired" | "unconfirmed" | "cold";

interface WhatsAppContactButtonProps {
  type: ContactType;
  user: {
    id?: string | null;
    first_name?: string | null;
    email?: string | null;
    whatsapp?: string | null;
    phone?: string | null;
    plan?: string | null;
    subscription_end_date?: string | null;
    created_at?: string | null;
  };
}

const typeConfig: Record<
  ContactType,
  {
    icon: LucideIcon;
    label: string;
    builder: (u: WhatsAppContactButtonProps["user"]) => string;
    className: string;
  }
> = {
  expired: {
    icon: MessageCircle,
    label: "Plano vencido — enviar oferta de renovação",
    builder: buildExpiredMessage,
    className: "text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20",
  },
  unconfirmed: {
    icon: MailWarning,
    label: "Email não confirmado — orientar ativação",
    builder: buildUnconfirmedMessage,
    className: "text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20",
  },
  cold: {
    icon: Snowflake,
    label: "Lead frio — reengajar usuário free",
    builder: buildColdLeadMessage,
    className: "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20",
  },
};

const WhatsAppContactButton: React.FC<WhatsAppContactButtonProps> = ({ type, user }) => {
  const config = typeConfig[type];
  const Icon = config.icon;
  const phone = normalizeWhatsAppNumber(user.whatsapp || user.phone);
  const disabled = !phone;

  const handleClick = () => {
    if (!phone) return;
    const message = config.builder(user);
    const url = buildWhatsAppUrl(phone, message);
    window.open(url, "_blank", "noopener,noreferrer");

    if (user.id) {
      void recordAdminContact({
        targetUserId: user.id,
        contactType: type,
        channel: "whatsapp",
        notes: config.label,
      });
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={handleClick}
            className={config.className}
          >
            <Icon size={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {disabled ? "Sem telefone cadastrado" : config.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default WhatsAppContactButton;
